import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import cn from 'classnames';
import { useGaudiLocalization } from '../../Provider/Provider.js';
import { useProxiedRef } from '../../../utils/hooks/useProxiedRef.js';
import { useBoundingClientRectListener } from '../../../utils/hooks/useBoundingClientRectListener.js';
import { useTable } from '../hooks/useTable.js';
import { Row } from '../util/renderRow.js';
import { BaseTable, DefaultEmptyState } from './BaseTable.js';
import { VariableSizeList, areEqual } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

var _excluded = ["height"],
    _excluded2 = ["emptyStateRenderer", "length", "loadMore"];
var ROW_HEIGHT = 40;
var VariableRow = /*#__PURE__*/React__default.memo(function (_ref) {
  var data = _ref.data,
      index = _ref.index,
      _ref$style = _ref.style,
      style = _objectWithoutPropertiesLoose(_ref$style, _excluded);

  var _useGaudiLocalization = useGaudiLocalization(),
      texts = _useGaudiLocalization.texts;

  var rows = data.rows,
      setRowHeight = data.setRowHeight,
      rowProps = data.rowProps,
      tableProps = data.tableProps,
      instance = data.instance,
      prepareRow = data.prepareRow,
      rowHeights = data.rowHeights;
  var row = rows[index];
  var ref = React__default.useRef(null);
  React__default.useEffect(function () {
    if (ref !== null && ref !== void 0 && ref.current) {
      setRowHeight(index, ref.current.getBoundingClientRect().height);
    }
  }, [rowHeights[index]]);

  if (row) {
    prepareRow(row, index);
    return React__default.createElement(Row, Object.assign({}, rowProps, {
      style: style,
      key: index,
      index: index,
      row: row,
      instance: instance,
      headerGroups: tableProps.headerGroups,
      setRowHeight: setRowHeight,
      ref: ref
    }));
  }

  return React__default.createElement("div", {
    className: "yt-table__row",
    role: "row",
    style: style
  }, React__default.createElement("div", {
    className: "yt-table__cell text-grey-dark"
  }, texts.table.loading));
}, areEqual);

var getAverageRowHeight = function getAverageRowHeight(rowHeights) {
  if (rowHeights === void 0) {
    rowHeights = {};
  }

  var keys = Object.keys(rowHeights);
  var estimatedHeight = keys.reduce(function (p, i) {
    return p + rowHeights[i];
  }, 0);
  return estimatedHeight / keys.length;
};

var WindowedTable = /*#__PURE__*/React__default.forwardRef(function WindowedTable(props, ref) {
  var tableRef = useProxiedRef(ref);

  var _props$emptyStateRend = props.emptyStateRenderer,
      emptyStateRenderer = _props$emptyStateRend === void 0 ? DefaultEmptyState : _props$emptyStateRend,
      length = props.length,
      loadMore = props.loadMore,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded2);

  var _useTable = useTable(_extends({}, otherProps, {
    windowed: true
  }), tableRef),
      rowProps = _useTable.rowProps,
      tableProps = _useTable.tableProps,
      rows = _useTable.rows,
      prepareRow = _useTable.prepareRow,
      instance = _useTable.instance,
      state = _useTable.state; // determine heights for windowing calculations


  var headerRef = React__default.useRef(null);
  var rowsRef = React__default.useRef(null);
  var tableDimensions = useBoundingClientRectListener(tableRef);
  var headerDimensions = useBoundingClientRectListener(headerRef);
  var height = tableDimensions && headerDimensions ? tableDimensions.height - headerDimensions.height : null; // variable row height calculations

  var estimatedRowHeight = rowProps.rowHeight || ROW_HEIGHT;
  var rowHeights = React__default.useRef({});
  var setRowHeight = React__default.useCallback(function (index, size) {
    if (rowHeights.current[index] !== size) {
      var _extends2;

      rowHeights.current = _extends({}, rowHeights.current, (_extends2 = {}, _extends2[index] = size, _extends2));

      if (rowsRef.current) {
        rowsRef.current.resetAfterIndex(0);
      }
    }
  }, []);
  var getRowHeight = React__default.useCallback(function (index) {
    return rowHeights.current[index] || estimatedRowHeight;
  }, []);
  React__default.useEffect(function () {
    if (rowsRef && rowsRef.current && rowProps.activeIndex !== undefined) {
      rowsRef.current.scrollToItem(rowProps.activeIndex);
    }
  }, [rowProps.activeIndex]); // trigger recalculation of variable row heights if the data changes

  React__default.useEffect(function () {
    rowHeights.current = {};

    if (rowsRef.current) {
      rowsRef.current.resetAfterIndex(0);
    }
  }, [rows.length]);
  var contentHeight = estimatedRowHeight * props.data.length || 0;
  var isScrollbarVisible = height !== null ? contentHeight > height : false;
  var className = cn(tableProps.className, 'yt-table--windowed', {
    'table-with-scrollbar': isScrollbarVisible
  });
  var list;
  var itemData = {
    rows: rows,
    setRowHeight: setRowHeight,
    rowProps: rowProps,
    tableProps: tableProps,
    instance: instance,
    prepareRow: prepareRow,
    state: state,
    rowHeights: rowHeights.current
  };

  if (height && rows.length) {
    var listProps = {
      height: height,
      itemData: itemData,
      estimatedItemSize: getAverageRowHeight(rowHeights.current),
      itemSize: getRowHeight,
      width: '100%'
    };

    if (loadMore && length) {
      var isLoaded = function isLoaded(index) {
        return !!rows[index];
      };

      list = React__default.createElement(InfiniteLoader, {
        isItemLoaded: isLoaded,
        itemCount: length,
        loadMoreItems: loadMore
      }, function (_ref2) {
        var onItemsRendered = _ref2.onItemsRendered,
            _ref3 = _ref2.ref;
        return React__default.createElement(VariableSizeList, Object.assign({}, listProps, {
          itemCount: length,
          onItemsRendered: onItemsRendered,
          ref: function ref(list) {
            _ref3(list);

            rowsRef.current = list;
          }
        }), VariableRow);
      });
    } else {
      list = React__default.createElement(VariableSizeList, Object.assign({}, listProps, {
        itemCount: rows.length,
        ref: rowsRef
      }), VariableRow);
    }
  }

  return React__default.createElement(BaseTable, Object.assign({}, tableProps, {
    className: className,
    headerRef: headerRef,
    tabIndex: 0,
    ref: tableRef
  }), list ? list : emptyStateRenderer());
});

WindowedTable.Column = function () {
  return null;
};

WindowedTable.Group = function () {
  return null;
};

export { WindowedTable };
//# sourceMappingURL=WindowedTable.js.map
